import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { Send } from 'lucide-react';
import { ErrorMessage } from '@edusense/shared-ui-kit';
import { FeedbackMessage } from '@edusense/shared-ui-content';
import { ContentMarkdownRenderer as MarkdownRenderer } from '../../content-studio/ContentStudio';
import { parseContentDocument } from '../../content-studio/document.mjs';
import { useLectureChat } from './hooks';
import { CardContainer, CardHeader, CardContent, ContentArea, MarkdownContainer, MarkdownWrapper, LectureDescription, CardTitle, SplitToggle, SplitGrid, SplitPane, GlossArticle, LookupCard } from './base-styles';
import { PasteToChatButton, SelectionSendButton } from './button-styles';
import { clearTextSelection, extractGlossary, isDragLookup, isLookupCancelKey, lookupGloss, resolveSplitMode, splitBlocks } from '../../learn-lookup/authored-gloss.mjs';
import { inferSourceLang, inferTargetLang, translateWithGoogle } from '../../learn-lookup/google-translate.mjs';
import ko from '../../i18n/locales/ko.json';
import en from '../../i18n/locales/en.json';
function authoredGlossNodes(blocks, credit, empty) {
    const hasAuthored = blocks.some((block)=>block.sentence);
    if (!hasAuthored) return /*#__PURE__*/ _jsx("p", {
        children: empty
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            blocks.map((block, index)=>/*#__PURE__*/ _jsx("p", {
                    children: block.sentence
                }, `s:${index}`)),
            /*#__PURE__*/ _jsx("small", {
                children: credit
            })
        ]
    });
}
function lectureViewerCopy() {
    const lang = typeof document === 'undefined' ? 'ko' : document.documentElement.lang;
    const pack = String(lang).toLowerCase().startsWith('en') ? en : ko;
    return pack['lectureViewer'];
}
const LectureViewer = ({ lecture, isLoading, error, onQuizSubmit })=>{
    const { isPasting, selectedText, selectionPosition, isVSCode, markdownContainerRef, handleMouseUp, handlePasteSelectedMarkdownToChat, handlePasteToChat } = useLectureChat();
    const copy = lectureViewerCopy();
    const [splitOn, setSplitOn] = useState(false);
    const [lookup, setLookup] = useState(null);
    const dragOrigin = useRef(null);
    const glossPaneRef = useRef(null);
    const rawMarkdown = lecture?.markdownContent ?? '';
    const videoUrl = lecture?.videoUrl;
    const title = lecture?.title;
    const editorialDocument = useMemo(()=>{
        try {
            return parseContentDocument(rawMarkdown);
        } catch  {
            return null;
        }
    }, [
        rawMarkdown
    ]);
    const enrichedMarkdown = useMemo(()=>{
        if (videoUrl && !editorialDocument && rawMarkdown.includes(videoUrl) === false) {
            return `:::video{src="${videoUrl}" title="${title || ''}"}\n:::\n\n${rawMarkdown}`;
        }
        return rawMarkdown;
    }, [
        rawMarkdown,
        videoUrl,
        title,
        editorialDocument
    ]);
    const parsed = useMemo(()=>extractGlossary(enrichedMarkdown), [
        enrichedMarkdown
    ]);
    const glossBlocks = useMemo(()=>splitBlocks(enrichedMarkdown), [
        enrichedMarkdown
    ]);
    const splitMode = useMemo(()=>{
        const source = inferSourceLang(parsed.body);
        return resolveSplitMode({
            markdown: enrichedMarkdown,
            glossary: parsed.glossary,
            supportedLanguages: lecture?.supportedLanguages,
            localizedMarkdown: lecture?.localizedMarkdown,
            targetLang: inferTargetLang(source)
        });
    }, [
        parsed,
        lecture
    ]);
    const [translated, setTranslated] = useState([]);
    const [translateState, setTranslateState] = useState('idle');
    useEffect(()=>{
        if (editorialDocument || !splitOn || splitMode.mode !== 'google') return;
        let cancelled = false;
        setTranslateState('loading');
        Promise.all(glossBlocks.map((block)=>{
            const source = inferSourceLang(block.source);
            return translateWithGoogle(block.source, {
                source,
                target: inferTargetLang(source),
                proxyUrl: '/api/learn/translate'
            });
        })).then((lines)=>{
            if (cancelled) return;
            setTranslated(lines);
            setTranslateState('idle');
        }).catch(()=>{
            if (cancelled) return;
            setTranslated([]);
            setTranslateState('error');
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        editorialDocument,
        splitOn,
        splitMode,
        glossBlocks
    ]);
    const onSourceScroll = (event)=>{
        const from = event.currentTarget;
        const to = glossPaneRef.current;
        if (!to) return;
        const maxFrom = from.scrollHeight - from.clientHeight;
        const maxTo = to.scrollHeight - to.clientHeight;
        if (maxFrom <= 0 || maxTo <= 0) return;
        to.scrollTop = from.scrollTop / maxFrom * maxTo;
    };
    const dismissLookup = ()=>{
        setLookup(null);
        clearTextSelection();
    };
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (isLookupCancelKey(event.key)) dismissLookup();
        };
        document.addEventListener('keydown', onKeyDown);
        return ()=>document.removeEventListener('keydown', onKeyDown);
    }, []);
    const onLookupMouseDown = (event)=>{
        dragOrigin.current = {
            x: event.clientX,
            y: event.clientY
        };
    };
    const onLookupMouseUp = (event)=>{
        handleMouseUp();
        const origin = dragOrigin.current;
        dragOrigin.current = null;
        if (!origin || !isDragLookup(origin, {
            x: event.clientX,
            y: event.clientY
        })) {
            dismissLookup();
            return;
        }
        const selection = window.getSelection();
        const text = String(selection?.toString() || '').trim();
        const container = markdownContainerRef.current;
        if (!text || !container || !selection?.anchorNode || !container.contains(selection.anchorNode)) {
            setLookup(null);
            return;
        }
        const hit = lookupGloss(text, parsed.glossary);
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        const box = container.getBoundingClientRect();
        if (hit) {
            setLookup({
                word: hit.word,
                gloss: hit.gloss,
                top: rect.bottom - box.top + 8,
                left: Math.max(8, rect.left - box.left)
            });
            return;
        }
        if (splitMode.mode !== 'google') {
            setLookup({
                word: text,
                gloss: copy.noAuthoredGloss,
                top: rect.bottom - box.top + 8,
                left: Math.max(8, rect.left - box.left)
            });
            return;
        }
        const source = inferSourceLang(text);
        setLookup({
            word: text,
            gloss: copy.translating,
            top: rect.bottom - box.top + 8,
            left: Math.max(8, rect.left - box.left)
        });
        translateWithGoogle(text, {
            source,
            target: inferTargetLang(source),
            proxyUrl: '/api/learn/translate'
        }).then((gloss)=>{
            setLookup((current)=>current && current.word === text ? {
                    ...current,
                    gloss: gloss || copy.translateFailed
                } : current);
        }).catch(()=>{
            setLookup((current)=>current && current.word === text ? {
                    ...current,
                    gloss: copy.translateFailed
                } : current);
        });
    };
    if (isLoading) return /*#__PURE__*/ _jsx(FeedbackMessage, {
        message: "콘텐츠 로딩 중...",
        type: "info"
    });
    if (error) return /*#__PURE__*/ _jsx(ErrorMessage, {
        message: `강의 로딩 오류: ${error}`
    });
    if (!lecture) return /*#__PURE__*/ _jsx(FeedbackMessage, {
        message: "강의 정보를 표시할 수 없습니다.",
        type: "info"
    });
    const original = /*#__PURE__*/ _jsxs(MarkdownContainer, {
        ref: markdownContainerRef,
        onMouseDown: onLookupMouseDown,
        onMouseUp: onLookupMouseUp,
        children: [
            /*#__PURE__*/ _jsxs(MarkdownWrapper, {
                children: [
                    editorialDocument && videoUrl && /^https?:\/\//.test(videoUrl) ? /*#__PURE__*/ _jsx("video", {
                        src: videoUrl,
                        controls: true,
                        style: {
                            maxWidth: '100%'
                        }
                    }) : null,
                    /*#__PURE__*/ _jsx(MarkdownRenderer, {
                        markdown: parsed.body,
                        onQuizSubmit: onQuizSubmit,
                        testId: "markdown-content"
                    })
                ]
            }),
            lookup && /*#__PURE__*/ _jsxs(LookupCard, {
                "data-testid": "lecture-lookup",
                style: {
                    top: `${lookup.top}px`,
                    left: `${lookup.left}px`
                },
                onMouseDown: (event)=>{
                    event.stopPropagation();
                    dragOrigin.current = null;
                },
                onClick: (event)=>{
                    event.stopPropagation();
                    dismissLookup();
                },
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        children: lookup.word
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: lookup.gloss || copy.noAuthoredGloss
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-label": copy.closeLookup,
                        children: "×"
                    })
                ]
            }),
            selectedText && selectionPosition && isVSCode && /*#__PURE__*/ _jsxs(SelectionSendButton, {
                style: {
                    top: `${selectionPosition.top}px`,
                    left: `${selectionPosition.left}px`
                },
                onClick: (event)=>{
                    event.stopPropagation();
                    handlePasteSelectedMarkdownToChat(selectedText);
                },
                title: copy.sendSelected,
                children: [
                    /*#__PURE__*/ _jsx(Send, {
                        size: 14
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: copy.sendSelected
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(CardContainer, {
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                children: [
                    title ? /*#__PURE__*/ _jsx(CardTitle, {
                        children: title
                    }) : null,
                    !editorialDocument && /*#__PURE__*/ _jsx(SplitToggle, {
                        type: "button",
                        "aria-pressed": splitOn,
                        "data-testid": "lecture-split-toggle",
                        onClick: ()=>setSplitOn((on)=>!on),
                        children: copy.splitView
                    }),
                    isVSCode && /*#__PURE__*/ _jsx(PasteToChatButton, {
                        onClick: ()=>handlePasteToChat(lecture),
                        disabled: isPasting,
                        title: "전체 강의 내용 채팅에 붙여넣기",
                        children: /*#__PURE__*/ _jsx(MessageSquarePlus, {
                            size: 16
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    /*#__PURE__*/ _jsx(ContentArea, {
                        children: enrichedMarkdown ? splitOn && !editorialDocument ? /*#__PURE__*/ _jsxs(SplitGrid, {
                            "data-testid": "lecture-split",
                            children: [
                                /*#__PURE__*/ _jsx(SplitPane, {
                                    onScroll: onSourceScroll,
                                    "aria-label": copy.splitOriginal,
                                    children: original
                                }),
                                /*#__PURE__*/ _jsx(SplitPane, {
                                    ref: glossPaneRef,
                                    "aria-label": splitMode.mode === 'google' ? copy.splitGloss : copy.splitAuthored,
                                    children: /*#__PURE__*/ _jsx(GlossArticle, {
                                        "data-testid": "lecture-split-gloss",
                                        children: splitMode.mode === 'localized' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(MarkdownRenderer, {
                                                    markdown: extractGlossary(splitMode.markdown || '').body,
                                                    onQuizSubmit: onQuizSubmit,
                                                    testId: "lecture-split-localized"
                                                }),
                                                /*#__PURE__*/ _jsx("small", {
                                                    children: copy.courseCredit
                                                })
                                            ]
                                        }) : splitMode.mode === 'authored' ? authoredGlossNodes(glossBlocks, copy.courseCredit, copy.noAuthoredGloss) : splitMode.mode === 'missing' ? /*#__PURE__*/ _jsx("p", {
                                            children: copy.noCourseTranslation
                                        }) : translateState === 'loading' ? /*#__PURE__*/ _jsx("p", {
                                            children: copy.translating
                                        }) : translateState === 'error' ? /*#__PURE__*/ _jsx("p", {
                                            children: copy.translateFailed
                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                translated.map((line, index)=>/*#__PURE__*/ _jsx("p", {
                                                        children: line
                                                    }, `${index}:${line}`)),
                                                translated.length > 0 ? /*#__PURE__*/ _jsx("small", {
                                                    children: copy.googleCredit
                                                }) : null
                                            ]
                                        })
                                    })
                                })
                            ]
                        }) : original : /*#__PURE__*/ _jsx("div", {
                            children: copy.noContent
                        })
                    }),
                    lecture.description && /*#__PURE__*/ _jsxs(LectureDescription, {
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                children: copy.summary
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: lecture.description
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default LectureViewer;
